import React, { useState } from "react";
import { StatCard, MetaCell, Expandable, DataTable, SectionTitle, LastSynced, EmptyState } from "./DashboardPrimitives";

const GithubDashboard = ({ data, formatLastConnected }) => {
  const [openRepo, setOpenRepo] = useState(null);
  const toggleRepo = (id) => setOpenRepo(openRepo === id ? null : id);

  if (!data?.connector_data) return <EmptyState message="No GitHub data available" />;

  const { identity, repositories } = data.connector_data;

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
        {identity.avatar_url && (
          <img src={identity.avatar_url} alt="avatar" className="w-10 h-10 rounded-full border border-gray-200 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">@{identity.login} <span className="text-gray-400 font-normal">· {identity.type}</span></p>
          <LastSynced value={data?.last_synced_at} formatFn={formatLastConnected} />
        </div>
      </div>

      {/* Account snapshot */}
      <div>
        <SectionTitle title="Account Snapshot" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Public Repos" value={identity.public_repos} />
          <StatCard label="Private Repos" value={identity.total_private_repos} />
          <StatCard label="Collaborators" value={identity.collaborators} />
          <StatCard label="2FA Enabled" value={identity.two_factor_authentication ? "Yes" : "No"} danger={!identity.two_factor_authentication} />
        </div>
      </div>

      {/* Repo overview */}
      <div>
        <SectionTitle title="Repository Overview" />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Total Repositories" value={repositories.length} />
          <StatCard label="Private Repositories" value={repositories.filter((r) => r.repository.private).length} />
          <StatCard label="Unique Members" value={[...new Set(repositories.flatMap((r) => r.members.map((m) => m.login)))].length} />
        </div>
      </div>

      {/* Per-repo */}
      <div className="space-y-3">
        <SectionTitle title="Repositories" count={repositories.length} />
        {repositories.map((repoData) => {
          const repo = repoData.repository;
          return (
            <Expandable
              key={repo.id}
              title={repo.name}
              isOpen={openRepo === repo.id}
              onToggle={() => toggleRepo(repo.id)}
              badge={
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                  style={{ backgroundColor: repo.private ? "#6B7280" : "var(--app-primary-color, #26256b)" }}
                >
                  {repo.private ? "Private" : "Public"}
                </span>
              }
            >
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <MetaCell label="Language" value={repo.language || "—"} />
                  <MetaCell label="Default Branch" value={repo.default_branch} />
                  <MetaCell label="Visibility" value={repo.visibility} />
                </div>
                <DataTable
                  title="Branches"
                  headers={["Branch Name", "Protected", "Commit SHA"]}
                  rows={repoData.branches.map((b) => [b.name, b.protected ? "Yes" : "No", b.commit.sha.slice(0, 7)])}
                />
                <DataTable
                  title="Members"
                  headers={["User", "Role", "Permissions"]}
                  rows={repoData.members.map((m) => [m.login, m.role_name, Object.keys(m.permissions).filter((p) => m.permissions[p]).join(", ")])}
                />
              </div>
            </Expandable>
          );
        })}
      </div>
    </div>
  );
};

export default GithubDashboard;
